import React, { memo, useEffect, useRef } from 'react';
import { AppState } from 'react-native';

import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import {
    APP_ONLINE_PRESENCE_CHANNEL,
    touchOnlinePresence,
} from '../services/onlinePresenceService';

const HEARTBEAT_MS = 30000;

const OnlinePresenceTracker = memo(() => {
    const { user, profile } = useAuth();
    const channelRef = useRef(null);
    const heartbeatTimerRef = useRef(null);
    const isTrackedRef = useRef(false);

    useEffect(() => {
        if (!user?.id) return undefined;

        const channel = supabase.channel(APP_ONLINE_PRESENCE_CHANNEL, {
            config: {
                presence: {
                    key: user.id,
                },
            },
        });

        channelRef.current = channel;

        const stopHeartbeat = () => {
            clearInterval(heartbeatTimerRef.current);
            heartbeatTimerRef.current = null;
        };

        const startHeartbeat = () => {
            stopHeartbeat();
            touchOnlinePresence(user, profile);
            heartbeatTimerRef.current = setInterval(() => {
                touchOnlinePresence(user, profile);
            }, HEARTBEAT_MS);
        };

        const trackRealtimePresence = async () => {
            if (!channelRef.current) return;

            try {
                await channelRef.current.track({
                    user_id: user.id,
                    username: profile?.username || profile?.nome || user.email?.split('@')?.[0] || 'usuario',
                    online_at: new Date().toISOString(),
                });
                isTrackedRef.current = true;
            } catch (error) {
                console.error('[Presence] Erro ao registrar online:', error.message);
            }
        };

        const untrackRealtimePresence = async () => {
            if (!channelRef.current || !isTrackedRef.current) return;

            try {
                await channelRef.current.untrack();
                isTrackedRef.current = false;
            } catch (error) {
                console.error('[Presence] Erro ao remover online:', error.message);
            }
        };

        const goOnline = () => {
            startHeartbeat();
            trackRealtimePresence();
        };

        const goOffline = () => {
            stopHeartbeat();
            untrackRealtimePresence();
        };

        const appStateSubscription = AppState.addEventListener('change', nextState => {
            if (nextState === 'active') {
                goOnline();
            } else {
                goOffline();
            }
        });

        if (AppState.currentState === 'active') {
            startHeartbeat();
        }

        channel.subscribe(status => {
            if (status === 'SUBSCRIBED' && AppState.currentState === 'active') {
                trackRealtimePresence();
            }
        });

        return () => {
            appStateSubscription.remove();
            stopHeartbeat();
            untrackRealtimePresence();
            supabase.removeChannel(channel);
            channelRef.current = null;
            isTrackedRef.current = false;
        };
    }, [profile?.nome, profile?.username, user?.email, user?.id]);

    return null;
});

export default OnlinePresenceTracker;
